import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AnalyticEvent, AnalyticsBloc, AnalyticsPageName } from '@kanalytics';
import { LandingButtonAnalyticsData, LandingDynamicBloc, SubscriptionBloc } from '@kbloc';
import { EnvironmentVariablesService } from '@kenv';
import { GroupValidationBloc } from '@kp/auth/group-validation.bloc';
import { DataLinkBloc } from '@kp/data-link/data-link.bloc';
import { OnboardingBloc } from '@kp/onboarding/onboarding.bloc';
import { PulseSurveyBloc } from '@kp/pulse-survey/pulse-survey.bloc';
import { FirstVisitBloc } from '@kp/shared/first-visit/first-visit.bloc';
import { UserBloc } from '@kp/user/user.bloc';
import { DataStoreService, WINDOW } from '@kservice';
import { EligibilityVerificationType, Product, UserEvent, UserType } from '@ktypes/enums';
import { AuthData, DataStatus, Group, LandingPage, Status, SubscriptionStatus } from '@ktypes/models';
import { RedirectToCheckoutOptions } from '@stripe/stripe-js';
import { loadStripe } from '@stripe/stripe-js/pure';
import { DeviceDetectorService } from 'ngx-device-detector';
import { combineLatest, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'kp-screen-landing-dynamic',
  templateUrl: './screen-landing-dynamic.component.html',
  styleUrls: ['./screen-landing-dynamic.component.scss'],
})
export class ScreenLandingDynamicComponent implements OnInit, OnDestroy {
  isLoading = false;
  landingPageStatus: DataStatus<LandingPage>;
  isPurposeful: boolean;
  isResourceful: boolean;

  private _authData: AuthData;
  private _destroy$ = new Subject<void>();
  private _gettingSubscriptionStatus = false;
  private _groupData: Group;
  private _hasSubscriptionPlans = false;
  private _stripeStatus: SubscriptionStatus;
  private _subscriptionAccessFound = false;
  private _verificationTypes: EligibilityVerificationType[] = [];

  constructor(
    private _analyticsBloc: AnalyticsBloc,
    private _dataLinkBloc: DataLinkBloc,
    private _dataStoreService: DataStoreService,
    private _deviceDetectorService: DeviceDetectorService,
    private _environmentVariablesService: EnvironmentVariablesService,
    private _firstVisitBloc: FirstVisitBloc,
    private _groupValidationBloc: GroupValidationBloc,
    private _landingDynamicBloc: LandingDynamicBloc,
    private _onboardingBloc: OnboardingBloc,
    private _pulseSurveyBloc: PulseSurveyBloc,
    private _router: Router,
    private _subscriptionBloc: SubscriptionBloc,
    private _userBloc: UserBloc,
    @Inject(WINDOW) private _window: Window
  ) {}

  ngOnInit(): void {
    this.isLoading = true;
    this.isPurposeful = this._environmentVariablesService.product === Product.purposeful;
    this.isResourceful = this._environmentVariablesService.product === Product.resourceful;

    // reset user candidate data if Resourceful or user didn't just follow a dataLink
    if (!this._dataLinkBloc.justFollowedADataLink) {
      this._onboardingBloc.setEligibilityCandidateData();
    }
    this._dataLinkBloc.resetJustFollowedADataLink();

    // clear any set onboarding flags
    this._landingDynamicBloc.resetLanding();

    this._dataStoreService.authData$
      .pipe(takeUntil(this._destroy$))
      .subscribe((authData) => (this._authData = authData));

    this._groupValidationBloc.groupValidationDataStatus$
      .pipe(takeUntil(this._destroy$))
      .subscribe(this._handleGroupValidationSubscription.bind(this));

    this._subscriptionBloc.subscriptionStatus$
      .pipe(takeUntil(this._destroy$))
      .subscribe(this._handleSubscriptionStatus.bind(this));

    this._landingDynamicBloc.landingPage$
      .pipe(takeUntil(this._destroy$))
      .subscribe(this._handleLandingPageSubscription.bind(this));

    combineLatest([
      this._dataStoreService.authStatus$,
      this._groupValidationBloc.groupValidationDataStatus$,
      this._dataStoreService.authData$,
    ])
      .pipe(takeUntil(this._destroy$))
      .subscribe(this._handleAuthStatusSubscription.bind(this));

    if (!this._groupValidationBloc.groupValidationDataStatus) {
      this._groupValidationBloc.startValidatingGroupCode();
    }
  }

  ngOnDestroy() {
    this._destroy$.next();
    this._destroy$.complete();
  }

  proceedWithOnboarding(landingButtonAnalyticsData: LandingButtonAnalyticsData) {
    if (this._groupData?.subscriptionPlans?.length > 0) {
      void this._redirectToStripe();
    } else {
      this._onboardingBloc.sendOnboardingUserEvent(UserEvent.onboarding_web_app_button_press);
      this.sendAnalyticsEventWithData(landingButtonAnalyticsData);
      if (this._verificationTypes?.length > 0 && !this._onboardingBloc.getEligibilityCandidateData()) {
        return this._router.navigate(['/welcome/eligibility']);
      } else if (this.isPurposeful) {
        void this._router.navigate([
          this._deviceDetectorService.isDesktop()
            ? '/welcome/mobile-app/interstitial'
            : '/welcome/mobile-app/interstitial/mobile',
        ]);
      } else {
        return this._router.navigate(['/privacy-pledge']);
      }
    }
  }

  sendAnalyticsEventWithData(landingButtonAnalyticsData?: LandingButtonAnalyticsData) {
    this._analyticsBloc.sendEvent(
      this._analyticsBloc.createEventFromInteraction({
        page: AnalyticsPageName.landing,
        event: AnalyticEvent.landing_page_button_clicked,
        meta: landingButtonAnalyticsData,
      })
    );
  }

  private _handleGroupValidationSubscription(groupValidationDataStatus) {
    if (groupValidationDataStatus == null || groupValidationDataStatus?.status === Status.starting) {
      // wait till groupValidationDataStatus is populated and not starting before doing anything
      return;
    }

    const { status: groupValidationStatus } = groupValidationDataStatus;
    const { eligibilityVerificationTypes, groupCode, subscriptionPlans } = groupValidationDataStatus.data ?? {};

    if (groupValidationStatus === Status.error) {
      void this._router.navigate(['/welcome/error']);
      return;
    }

    this._verificationTypes = eligibilityVerificationTypes as EligibilityVerificationType[];

    if (groupValidationStatus === Status.done && groupCode) {
      this._groupData = groupValidationDataStatus.data;

      this._hasSubscriptionPlans = subscriptionPlans?.length > 0;
      this._landingDynamicBloc.getLandingPage(groupCode);
    }
  }

  private _handleSubscriptionStatus(subscriptionStatus) {
    if (subscriptionStatus == null || subscriptionStatus?.status === Status.starting) {
      return;
    }
    if (subscriptionStatus?.status === Status.done) {
      this._subscriptionAccessFound = true;
      this._stripeStatus = subscriptionStatus.data;
      this._gettingSubscriptionStatus = false;
    } else if (subscriptionStatus?.status === Status.error) {
      this._gettingSubscriptionStatus = false;
    }
  }

  private _handleLandingPageSubscription(landingPageStatus) {
    const { groupCode, theme } = this._groupData ?? {};
    const { status } = landingPageStatus ?? {};
    this.landingPageStatus = landingPageStatus;
    if (landingPageStatus == null || status === Status.starting) {
      // only proceed once landingPageStatus is populated and not starting
      return;
    }

    if (status === Status.error) {
      void this._router.navigate(['/welcome/error']);
      return;
    } else if (status === Status.done) {
      const pulseSurveyId = landingPageStatus?.data?.pulseSurveyId;
      const candidate = this._onboardingBloc.getEligibilityCandidateData();
      this.isLoading = false;
      this._pulseSurveyBloc.setPulseSurveyId(pulseSurveyId);

      if (
        !this._gettingSubscriptionStatus &&
        this._hasSubscriptionPlans &&
        this._authData?.token &&
        groupCode === this._authData?.user?.group?.groupCode
      ) {
        // if there is authData and subscription plans and no need to create a user
        // and if the groupCode hasn't changed, proceed to check subscription status
        this._gettingSubscriptionStatus = true;
        this._subscriptionBloc.getSubscriptionStatus();
      } else if (candidate?.candidateId) {
        // don't create an onboarding user is there is a candidate id from eligibility checking
        this._onboardingBloc.setLatestPage(
          this._dataStoreService.authData?.user?.type === UserType.pulse ? '/privacy-pledge' : '/welcome/notice'
        );
      } else {
        // authStatusSubscription will be triggered to fetch subscriptionStatus
        this._userBloc.createOnboardingUser(new Group().deserialize({ groupCode, theme }), null);
      }
      this._dataStoreService.setUser({
        ...this._dataStoreService.user,
        group: {
          ...this._dataStoreService.user?.group,
          ...this._groupData,
        },
      });
    }
  }

  private _handleAuthStatusSubscription([authDataStatus, groupValidationDataStatus, authData]) {
    if (
      authDataStatus?.status === Status.done &&
      authDataStatus?.data?.token &&
      !this._gettingSubscriptionStatus &&
      this._hasSubscriptionPlans &&
      this._groupData?.groupCode === authDataStatus?.data?.user?.group?.groupCode
    ) {
      this._gettingSubscriptionStatus = true;
      this._subscriptionBloc.getSubscriptionStatus();
    }
  }

  private async _redirectToStripe() {
    this._destroy$.next(); // stop observables from firing again before redirecting to Stripe
    if (this._stripeStatus?.access) {
      this._router.navigate(['/welcome/mobile-app/interstitial']);
    } else {
      const stripe = await loadStripe(environment.PUBLISHABLE_KEY);
      const domain = this._window.location.origin;
      const options: Partial<RedirectToCheckoutOptions> = {
        lineItems: [{ price: this._groupData?.subscriptionPlans?.[0]?.planId, quantity: 1 }],
        successUrl:
          this._deviceDetectorService.isMobile() || this._deviceDetectorService.isTablet()
            ? `${domain}/checkout/success/onboarding/mobile/{CHECKOUT_SESSION_ID}`
            : `${domain}/checkout/success/onboarding/{CHECKOUT_SESSION_ID}`,
        cancelUrl: `${domain}/checkout/onboarding/cancel`,
        mode: 'subscription',
        clientReferenceId: this._dataStoreService.user?.id,
      };

      await stripe.redirectToCheckout(options as RedirectToCheckoutOptions);
    }
  }
}
